'use client'
import Link from 'next/link'
import { useSearchParams } from 'next/navigation'
import { Suspense } from 'react'
import { getShopBrandPath } from '@/src/routes/routes'
import PriceComponent from '../../common/PriceComponent'
import MemberExclusivePrice from './MemberExclusivePrice'
import SingleProductCustomizationAndSelection from './SingleProductCustomizationAndSelection'
import type { ISingleProductAPIResponseData } from '@/src/types/singleProduct'
import AddToWishlist from './AddToWishlist'

interface SingleProductDetailsProps {
    data: ISingleProductAPIResponseData | null;
    isMember: boolean;
}

export default function SingleProductDetails({ data, isMember }: SingleProductDetailsProps) {
    const searchParams = useSearchParams()
    const color = searchParams.get('color') ?? data?.variants?.[0]?.color ?? ''
    const size = searchParams.get('size') ?? data?.variants?.[0]?.size ?? ''

    // Try exact color+size match → color-only match → first variant
    const variant = (
        data?.variants?.find(
            v =>
                v.color?.toLowerCase() === color.toLowerCase() &&
                v.size?.toLowerCase() === size.toLowerCase()
        ) ??
        data?.variants?.find(
            v => v.color?.toLowerCase() === color.toLowerCase()
        ) ??
        data?.variants?.[0]
    ) ?? null

    const isVariant = data?.is_variant || Boolean(variant);

    // Non-variant products carry price on data.variant; variant products use the matched variant
    const priceSource = isVariant ? variant : data?.variant
    const variantSku = isVariant
        ? (variant?.sku ?? '')
        : (data?.variant?.sku ?? data?.sku ?? '')

    const stock = isVariant
        ? (variant?.stock ?? 0)
        : (data?.variant?.stock ?? data?.stock ?? 0)

    return (
        <>
            <div className="flex items-start justify-between">
                <div className='space-y-1'>
                    <p className='text-sm'>
                        Brand:{' '}
                        {data?.brandId?.slug && data?.brandId?.name ? (
                            <Link
                                href={getShopBrandPath(data.brandId.slug)}
                                className="underline underline-offset-4 decoration-body-color/40 hover:decoration-body-color"
                            >
                                {data.brandId.name}
                            </Link>
                        ) : (
                            data?.brandId?.name
                        )}
                    </p>
                    <h1 className="product_title">{data?.title}</h1>
                </div>
                <AddToWishlist product_id={data?._id || ''} isAlreadyWishlist={data?.alreadyWishlist ?? false} />
            </div>

            {!data?.exclusive && (
                <div className={`${isMember ? 'flex items-center gap-4 divide-x divide-body-color/30' : ''}`}>
                    <p className='text-[15px] text-body-color/80 font-light pe-4'>
                        <PriceComponent title="Retail Price" lineThrough={true} price={priceSource?.rental_price ?? 0} currency={data?.pricing_context?.currency ?? '£'} />
                    </p>
                    <p className={`text-[15px] ${isMember ? 'text-body-color/80 font-light' : 'font-medium '}`}>
                        <PriceComponent title="Our Price" lineThrough={isMember} price={priceSource?.price ?? 0} currency={data?.pricing_context?.currency ?? '£'} />
                    </p>
                </div>
            )}

            <MemberExclusivePrice retailPrice={priceSource?.price ?? 0} memberPrice={priceSource?.membership_price ?? 0} currency={data?.pricing_context?.currency ?? '£'} isExclusive={data?.exclusive ?? false} isMember={isMember} />

            <Suspense fallback={<div>Loading...</div>}>
                <SingleProductCustomizationAndSelection
                    productId={data?._id ?? ''}
                    variantSku={variantSku}
                    currency={data?.pricing_context?.currency ?? '£'}
                    sizes={data?.sizes ?? []}
                    defaultColor={color}
                    defaultSize={size}
                    colors={data?.colors ?? []}
                    alreadyCart={data?.alreadyCart ?? false}
                    isVariant={isVariant}
                    isExclusive={data?.exclusive ?? false}
                    stock={stock}
                />
            </Suspense>
        </>
    )
}
